import { forwardRef } from 'react';
import MuiTypography, {
    TypographyProps as MuiTypographyProps,
} from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS } from '../../theme/legacy/typography';
import {
    DesignSystemColorModeFlattened,
    DesignSystemTypographyFontWeights,
    DesignSystemTypographyVariants,
} from '../../theme/design-system/types';
import { useSlotContext } from '../../utils/useSlotContext';
import { AvailableSlots } from '../../utils/Slots';

export interface TypographyProps
    extends Omit<MuiTypographyProps, 'variant' | 'fontWeight'> {
    /**
     * @default 'md'
     */
    variant?: DesignSystemTypographyVariants;
    /**
     * The font weight to use. Can be a predefined key or a numeric value.
     * Theme regular will be used if not provided.
     */
    fontWeight?: DesignSystemTypographyFontWeights | 'inherit';
    sx?: SxProps<Theme>;
    /**
     * The color of the text. Can be a design system color only.
     */
    color?: DesignSystemColorModeFlattened | 'inherit';
    /**
     * When true, applies CSS `text-wrap: balance` for more even line lengths.
     */
    balance?: boolean;
    /**
     * Slot name for use in component composition.
     */
    slot?: Extract<AvailableSlots, 'label'>;
}

const defaultVariant: DesignSystemTypographyVariants = 'md';
const defaultColor: DesignSystemColorModeFlattened = 'ds.fg.neutral.primary';

/**
 * Typography component wrapping MUI Typography to provide custom variants and font weights
 * based on the design system.
 */
export const Typography = forwardRef<HTMLElement, TypographyProps>(
    (props, ref) => {
        const mergedProps = useSlotContext(props, props.slot ?? 'label');
        const {
            variant = defaultVariant,
            fontWeight,
            color = defaultColor,
            balance,
            sx,
            slot,
            ...rest
        } = mergedProps;
        const resolvedVariant = DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS[variant];

        return (
            <MuiTypography
                ref={ref}
                variant={resolvedVariant}
                color={color}
                sx={{
                    fontWeight,
                    ...(balance && { textWrap: 'balance' }),
                    ...sx,
                }}
                {...rest}
            />
        );
    },
);
